/* ============================================================================
   GalleryGrid.tsx — mode 'gallery' : photos brutes dispersées aléatoirement
   sur le canvas (scatter collage). Position pseudo-aléatoire déterministe
   basée sur le hash de message.id pour stabilité entre renders.

   Pas de grille structurée — chaque photo est positionnée en absolute avec
   un x/y unique dérivé de son ID. Léger jitter de rotation pour effet
   polaroid déposé. Z-index = ordre temporel (récents par-dessus).

   Reçoit la liste des messages avec photoUrl != null. Affiche chaque photo
   en <img> natif (R2 servi via /api/photos/photos/<id>.<ext>).
   ============================================================================ */

import type { Message } from '@shared/types';

/** Hash FNV-1a déterministe message.id → 32-bit. */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Dérive 3 valeurs pseudo-aléatoires distinctes [0..1) depuis un seed. */
function rand3(seed: number): [number, number, number] {
  const a = ((seed * 0x9e3779b9) >>> 0) / 4294967296;
  const b = ((seed * 0x85ebca6b) >>> 0) / 4294967296;
  const c = ((seed * 0xc2b2ae35) >>> 0) / 4294967296;
  return [a, b, c];
}

const PHOTO_SIZE = 200;
const ROTATION_MAX_DEG = 8; // ±8° léger jitter polaroid

interface Props {
  messages: Message[];
}

export function GalleryGrid({ messages }: Props) {
  const photos = messages.filter((m) => m.photoUrl);

  if (photos.length === 0) {
    return (
      <div className="canvas-gallery-scatter canvas-gallery-scatter--empty" aria-hidden="true">
        <p className="canvas-gallery-scatter__empty-label">— aucune photo dans la galerie —</p>
      </div>
    );
  }

  // Tri par createdAt ascendant : les plus récents en dernier → z-index plus élevé
  const sorted = [...photos].sort((a, b) => a.createdAt - b.createdAt);

  return (
    <div className="canvas-gallery-scatter" data-testid="canvas-gallery-scatter">
      {sorted.map((m, idx) => {
        const seed = hash(m.id);
        const [rx, ry, rrot] = rand3(seed);
        // Positions en pourcentage : 5%..95% pour éviter de coller aux bords
        // (photo size 200 sur canvas variable → pourcentage déduit côté CSS).
        const xPct = 5 + rx * 90;
        const yPct = 5 + ry * 90;
        const rotation = (rrot - 0.5) * 2 * ROTATION_MAX_DEG;
        return (
          <figure
            key={m.id}
            className="canvas-gallery-scatter__item"
            style={{
              left: `calc(${xPct}% - ${PHOTO_SIZE / 2}px)`,
              top: `calc(${yPct}% - ${PHOTO_SIZE / 2}px)`,
              transform: `rotate(${rotation.toFixed(2)}deg)`,
              zIndex: idx + 1,
            }}
          >
            <img
              src={m.photoUrl!}
              alt={m.title}
              className="canvas-gallery-scatter__img"
              loading="lazy"
              decoding="async"
            />
            {m.title && (
              <figcaption className="canvas-gallery-scatter__caption">
                {m.title}
              </figcaption>
            )}
          </figure>
        );
      })}
    </div>
  );
}
